<template>
    <access-denied v-if="accessDenied" title="formas-pagamento" />
    <div class="page" v-else>
        <loading :isLoading="isLoading && !$store.getters.isLoading" />
        <!-- Título -->
        <div class="cliente vertical-flow">
            <div v-if="cameFrom == 'route'" class="listing-header">
                <div class="title-breadcrumb" >
                    <h1 class="title">{{ $t('clientes') }}</h1>
                    <nav class="breadcrumb" aria-label="breadcrumbs">
                        <ul>
                            <li>
                                <a class="a-inativo">{{ 
                                    isVenda ? $t('vendas') : $t('agro')
                                }}</a>
                            </li>
                            <li>
                                <a aria-current="page">{{ $t('clientes') }}</a>
                            </li>
                        </ul>
                    </nav>
                </div>
                <div class="header-buttons">
                    <button
                        v-if="!usuarioIntegracao && permissaoGeral.create && cameFrom == 'route'"
                        class="save-btn"
                        type="button"
                        tag="button"
                        @click="$router.push({ name: `${abrev}-cliente-dados-cadastrais-new` })"
                    >
                        {{ $t('adicionar') }}
                    </button>
                </div>
            </div>

            <!-- BUSCA -->
            <div class="search-and-filter">
                <div class="search-bar" id="filterTable" >
                    <search-bar :onSearch="debouncedOnSearch" tooltipLabel="msg-busca-clientes-campos" />
                </div>
            </div>

            <!-- FILTROS EXTRAS -->
            <div class="filtros-extras" id="filtro">
                <FiltroCliente
                    @scrollElement="elementToScroll"
                    @update-filtro="updateFiltro"
                    @load-clientes="carregarDados(1)"
                    ref="filtroRef"
                    :limparFiltros="limparFiltros" 
                    @filtrosLimpos="limparFiltros = $event"
                    @focusInFilter="goToElement = $event"
                />
            </div>

            <!-- BOTÕES -->
            <div class="columns mt-2 mb-1">
                <div class="column is-6 has-text-centered">
                    <button class="delete-btn pl-4 pr-4" @click="confirmCleanFilters">
                        {{ $t('limpar-filtros') }}
                    </button>
                </div>
                <div class="column is-6 has-text-centered">
                    <button class="save-btn pl-4 pr-4" @click="pesquisar">
                        {{ $t('pesquisar') }}
                    </button>
                </div>
            </div>

            <div class="clients-table registry-wrapper"
                    id="table" v-if="clients">
                <empty-table-message v-if="isEmpty" />
                <b-table v-else 
                    :data="clients" 
                    class="data-table" 
                    backend-sorting
                    @sort="sortTable"
                    >
                    <b-table-column 
                        v-for="(f, i) in fields"
                        :key="i"
                        :field="f.key"
                        :width="f.width"
                        :label="f.key != 'col-financeiro' 
                            ? f.key != 'col-situacao' 
                                ? f.key != 'col-localizacao' 
                                    ? $t(f.key) 
                                    : $t('localizacao')
                                : $t('situacao')
                            : ''"
                        :sortable="f.sortable"
                        :cell-class="f.cellClass"
                        v-slot="props"
                        >
                        <!-- BARRA DE STATUS FINANCEIRO -->
                        <div v-if="f.key == 'col-financeiro'">
                            <div 
                                v-if="props.row.possuititulosvencidos"
                                class="bar vencidos"
                                v-tippy 
                                :title="$t('col-financeiro') + ': ' + $t('contem-titulos-vencidos')"
                            />
                            <div v-else-if="props.row.possuititulosavencer"
                                class="bar a-vencer"
                                v-tippy 
                                :title="$t('col-financeiro') + ': ' + $t('contem-titulos-a-vencer')"
                            />
                        </div>
                        <div v-else-if="f.key == 'idclienteerp'">
                            <pre>{{ props.row.idclienteerp != null 
                                    ? props.row.idclienteerp
                                    : "---"
                                }}</pre>
                        </div>
                        <div v-else-if="f.key == 'idcliente'">
                            {{ props.row.idcliente }}
                        </div>
                        <div v-else-if="f.key == 'col-localizacao'">
                            <div class="has-text-centered" @click="openLocationEditor(props.row)">
                                <b-icon
                                    icon="map-marker"
                                    class="location-icon"
                                    :class="{ disabled: !containsLocation(props.row) }"
                                />
                            </div>
                        </div>
                        <div v-else-if="f.key == 'nome'">
                            <router-link
                                v-if="permissaoGeral.read"
                                :to="{
                                    name: `${abrev}-cliente-dados-cadastrais-edit`,
                                    params: { id: props.row.id },
                                }"
                            >
                                <pre>{{ props.row['nome'] }}</pre>
                            </router-link>
                            <span v-else>{{ props.row['nome'] }}</span>
                        </div>
                        <div v-else-if="f.key == 'rsocial'">
                            <router-link
                                v-if="permissaoGeral.read"
                                :to="{
                                    name: `${abrev}-cliente-dados-cadastrais-edit`,
                                    params: { id: props.row.id  },
                                }"
                            >
                                <pre>{{ props.row['rsocial'] }}</pre>
                            </router-link>
                            <span v-else>{{ props.row['rsocial'] }}</span>
                        </div>
                        <div v-else-if="f.key == 'col-dsv'">
                            {{ getDiasSemVenda(props.row) }}
                        </div>
                        <div v-else-if="f.key == 'col-ultima-visita'" class="nobreak">
                            {{
                                props.row['datahoraultimavisita'] == null
                                    ? $t('nunca-visitado')
                                    : $moment(props.row['datahoraultimavisita']).format('L')
                            }}
                        </div>
                        <div v-else-if="f.key == 'col-situacao'">
                            <b-icon
                                size="is-small"
                                icon="checkbox-blank-circle"
                                class="situacao-icon"
                                :style="`color: ${props.row.situacao.cor ? '#' + props.row.situacao.cor.replace('#', '') : '#f1f1f1'}; width: 15px;`"
                                v-tippy 
                                :title="$t('situacao') + ': ' + (
                                    props.row.situacao.descricao 
                                    ? props.row.situacao.descricao 
                                    : $t('sem-registro'))"
                            />
                        </div>
                        <div v-else-if="pdadetalhe['registro_enviado'] && !pdadetalhe['registro_enviado'].oculta">
                            <div class="status-cliente" v-if="f.key == 'status-origem-cliente'">
                                <span
                                    class="tag tag-status"
                                    :class="{
                                        'is-warning': props.row.registro_enviado == 'E',
                                        'is-orange': props.row.registro_enviado == 'C',
                                        'is-success': props.row.registro_enviado == 'S',
                                        'is-danger': props.row.registro_enviado == 'N' || props.row.registro_enviado == 'D'
                                        || props.row.registro_enviado == 'EI',
                                        'is-primary': ['ED', 'AP'].includes(props.row.registro_enviado),
                                        // @86380 - caso não for algo do sistema, então coloca uma cor diferente.
                                        'is-link': !props.row.registro_enviado.includes(['E', 'C', 'S', 'N']),
                                    }"
                                >
                                    {{ getStatus(props.row.registro_enviado) }}
                                </span>
                                <!-- @86380 - validacao das tags conforme a origem -->
                                <span
                                    v-if="origensFV.includes(props.row.origem)"
                                    class="tag is-success status-tag"
                                >
                                    {{ props.row.origem }}
                                </span>
                                <span
                                    v-else-if="origensB2B.includes(props.row.origem)"
                                    class="tag is-info status-tag"
                                >
                                    {{ props.row.origem }}
                                </span>
                                <span v-else class="tag is-warning status-tag">
                                    {{
                                        props.row.origem === 'sem-origem'
                                            ? $t(props.row.origem)
                                            : props.row.origem
                                    }}
                                </span>
                            </div>
                        </div>
                    </b-table-column>
                </b-table>

                <PaginationViewer
                    class="mb-0"
                    :resetPagination="pagination.resetPagination"
                    :totalItems="pagination.totalRows"
                    :perPageOptions="pagination.perPageOptions"
                    :optionStarted="pagination.optionStarted"
                    @change="updatePagination"
                />
            </div>

            <MapasGoogleMapsModal
                v-if="permissao.update"
                :center="center"
                :isUpdating="isUpdatingClient"
                :locationEditorOpen="locationEditorOpen"
                :update="true"
                @updateLocation="updateLocation"
                @onCancelLocationEditor="onCancelLocationEditor"
                :showSaveButton="permissaoGeral.update"
            />
        </div>
    </div>
</template>

<script>
import FilterBar from '@/components/shared/Filter';
import ContainerLoader from '@/components/content/loader/ContainerLoader';
import ClienteService from "@/services/vendas/cliente/ClienteService";
import FiltroCliente from '@/components/vendas/cliente/FiltroCliente';
import MapasGoogleMapsModal from '@/components/shared/maps/MapasGoogleMapsModal';

export default {
    components: {
        FilterBar,
        ContainerLoader,
        FiltroCliente,
        MapasGoogleMapsModal,
    },
    props: {
        cameFrom: {
            type: String,
            default: 'route',
        },
        reloadData: {
            type: Boolean,
            default: false,
        },
        abrev: {
            type: String,
            default: 'ven',
        },
    },
    data() {
        return {
            usuarioIntegracao: this.$store.state.user.tabelasERPWebServer.includes("CLIENTE"),
            clients: [],
            idClienteEdit: -1,
            service: new ClienteService(this.$http, this.$router),
            fields: [
                { key: 'col-financeiro', width: null, sortable: false, cellClass: 'table-td-relative' },
                { key: 'idclienteerp', width: null, sortable: true, cellClass: null },
                { key: 'idcliente', width: 80, sortable: true, cellClass: null },
                { key: 'nome', width: null, sortable: true, cellClass: null },
                { key: 'rsocial', width: null, sortable: true, cellClass: null },
                { key: 'col-dsv', width: null, sortable: true, cellClass: null },
                { key: 'col-ultima-visita', width: null, sortable: true, cellClass: null },
                { key: 'col-situacao', width: null, sortable: false, cellClass: null },
                { key: 'col-localizacao', width: null, sortable: false, cellClass: null },
                { key: 'status-origem-cliente', width: 80, sortable: false, cellClass: null },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            isOrdering: false,
            modalOpen: false,
            allowDelete: false,
            column: 'nome',
            order: 'asc',
            debouncedOnSearch: null,
            tooltipPosition: 'is-right',

            // configurações do mapa
            idCiente: null,
            filtros: {
                search: '',
                empresa: 0,
                vendedores: [],
                atividades: [],
                situacoes: [],
                cidades: [],
                regioes: [],
                statusComerciais: [],
                visita: 0,
                geoLocalizacao: 0,
                contatos: 0,
                naoVenda: 0,
                minDiasDesdeUltimaVenda: -1,
                minDiasDesdeUltimaAtualizacao: -1,
                // Filtros novos
                grupoeconomico: [],
                areainteresse: [],
                propriedade: '',
                planosafra: 0,
                status: 0,
                origem: 0, 
            },
            permissao: {},
            permissaoGeral: {},
            center: {
                lat: null,
                lng: null,
            },
            locationEditorOpen: false,
            cancelLocationEditor: false,
            isUpdatingClient: false,
            limparFiltros: false,
            goToElement: '',
            // @84380 - listas utilizados para verificar qual cor da tag será utilizado.
            origensFV: ['FV', 'WEBFV'],
            origensB2B: ['B2B MOBILE', 'B2B WEB'],

            pdadetalhe: {},
            pdadetalheHasData: false,

            isVenda: this.$route.meta.contexto == "vendas",
            isPromotor: this.$route.meta.contexto == "promotor",
            isAgro: this.$route.meta.contexto == "agro",

            accessDenied: false,
        };
    },
    watch: {
        limparFiltros(val) {
            if (!val) this.carregarDados();
        },
        reloadData(val) {
            if (val) this.carregarDados(this.pagination.currentPage);
        },
    },
    created() {
        this.pagination.perPage = this.pagination.perPageOptions[this.pagination.optionStarted];
        
        if(this.isVenda) {
            this.permissao = this.hasPermissionAccess(
                'vendas', 
                'clientes', 
                'ven-cliente'
            );
            this.permissaoGeral = this.hasPermissionAccess(
                'vendas', 
                'clientes', 
                'ven-cliente-dados-cadastrais-geral'
            );
        }else if(this.isPromotor) {
            this.permissao = this.hasPermissionAccess(
                'promotor', 
                'clientes', 
                'pro-cliente'
            );
            this.permissaoGeral = this.hasPermissionAccess(
                'promotor', 
                'clientes', 
                'pro-cliente-dados-cadastrais-geral'
            );
        }else if(this.isAgro){
            this.permissao = this.hasPermissionAccess(
                "agro",
                "clientes",
                "agro-cliente"
            );
            this.permissaoGeral = this.hasPermissionAccess(
                "agro",
                "clientes",
                "agro-cliente-dados-cadastrais-geral"
            );
        }

        if (!this.permissao.read) {
            this.accessDenied = true;
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'clientes' },
            });
        }
        this.getModulo("CLIENTE").detalhe.forEach((d) => {
                d.invalid = false;
                d.msg = "msg-campo-obrigatorio";
                this.$set(this.pdadetalhe, d.campo.toLowerCase(), d);
                // Caso tenha dados em pdadetalhe
                this.pdadetalheHasData = true;
            });
        this.debouncedOnSearch = this.debounce(this.onSearch, 500);
        if (this.$route.params.pageId) this.idClienteEdit = this.$route.params.pageId;

        let objWithIdIndex = this.fields.findIndex((obj) => obj.key == 'status-origem-cliente');

        if (!(this.pdadetalhe['registro_enviado'] && !this.pdadetalhe['registro_enviado'].oculta)) {
            if (objWithIdIndex > -1) {
                this.fields.splice(objWithIdIndex, 1);
            }
        }
    },
    methods: {
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        onSearch(value) {
            this.filtros.search = this.removeAcentos(value);
            this.filtros.search = this.filtros.search.replaceAll(/[./-]/g, '');
            this.carregarDados();
        },
        getDiasSemVenda(cliente) {
            let dataultimavenda = cliente['dataultimavenda'];
            if (dataultimavenda === null || typeof dataultimavenda === 'undefined') {
                return this.$t('nunca-vendido');
            } else {
                let now = this.$moment();
                let diasSemVenda = now.diff(dataultimavenda, 'days');
                return diasSemVenda + ' ' + this.$t(diasSemVenda > 1 ? 'x-dias' : 'x-dia');
            }
        },
        getStatus(status) {
            switch (status) {
                case 'E':
                    return this.$t('processando');
                case 'S':
                    return this.$t('transmitido');
                case 'N':
                    return this.$t('nao-enviado');
                case 'C':
                    return this.$t('cancelado');
                case 'A':
                    return this.$t('aprovado');
                case 'F':
                    return this.$t('faturado');
                case 'AP':
                    return this.$t('aguardando-aprovacao');
                case 'ED':
                    return this.$t('em-edicao');
                case 'D':
                    return this.$t('deletado');
                case "EI":
                    return this.$t("problema-integracao-erp");
                default:
                    return status === 'sem-status' ? this.$t(status) : status; //@86380 - caso pedido retorno não tenha um status, exibe Sem Status
            }
        },
        updateFiltro(filtro) {
            this.filtros[filtro.nomeFiltro] = filtro.valor;
        },
        pesquisar() {
            this.carregarDados();
            this.elementToScroll('table');
        },
        elementToScroll(id) {
            this.goToElement = id;
        },
        limparFiltrosCall() {
            this.limparFiltros = true;
        },
        confirmCleanFilters() {
            this.$root.confirm({
                message: this.$t("msg-confirm-limpar-filtros"),
                onConfirm:() => this.limparFiltrosCall(),
            });
        },
        carregarDados(currentPage = 1) {
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            this.isLoading = true;
            this.service
                .findAll(currentPage - 1, this.pagination.perPage, this.order, this.column, this.filtros)
                .then((value) => {
                    if (value.content.length > 0) {
                        this.pagination.totalRows = value.totalElements;
                        this.clients = value.content;
                        this.isEmpty = this.clients.length <= 0;
                    } else {
                        // @71185 - Inserido a atualização do número de elementos quando não há resultados
                        this.pagination.totalRows = value.totalElements;
                        this.isEmpty = true;
                    }
                })
                .catch((e) => {
                    this.isEmpty = this.clients.length <= 0;
                    console.error('Ocorreu algum erro: ', { e });
                })
                .finally(() => {
                    this.isLoading = false;
                    this.pagination.resetPagination = false;
                });
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        containsLocation(client) {
            return (
                client != null &&
                client.latitude != null &&
                client.longitude != null &&
                client.latitude != 0 &&
                client.longitude != 0
            );
        },

        openLocationEditor(cliente) {
            this.center.lat = +cliente.latitude;
            this.center.lng = +cliente.longitude;
            this.idCiente = cliente.idcliente;
            this.locationEditorOpen = true;
        },
        onCancelLocationEditor() {
            this.locationEditorOpen = false;
            this.center.lat = 0;
            this.center.lng = 0;
            this.idCiente = null;
        },
        updateLocation(val) {
            this.isLoading = true;
            this.center.lat = val.center.lat;
            this.center.lng = val.center.lng;
            let form = {
                idcliente: this.idCiente,
                latitude: this.center.lat,
                longitude: this.center.lng,
            };
            this.service
                .updateLocation(form)
                .then((body) => {
                    this.$root.showMessage('success', 'msg-dados-alterados');
                    this.carregarDados();
                    this.onCancelLocationEditor();
                })
                .catch((e) => {
                    console.error('Erro ao salvar a localização do cliente.', e);
                    this.$root.showMessage('error', 'msg-problema-realizar-acao');
                })
                .finally((_) => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
    .nobreak{
        word-break: initial;
    }
</style>

<style lang="scss" scoped>
.page {
    position: relative;
    padding-left: 0;
}

.clients-table {
    font-size: 14px;
}

.bar {
    position: absolute;
    top: 5px;
    bottom: 5px;
    left: 5px;
    right: 5px;
    border-radius: 100px;
    width: 5px;
    min-height: 25px;
    &.a-vencer {
        background-color: #f57c00;
    }
    &.vencidos {
        background-color: #d32f2f;
    }
}

.column-header-icon {
    margin-right: 0px !important;
    margin-left: 0px !important;
}


.cliente .search-and-filter {
    display: flex;
    justify-content: space-between;
    margin: 10px 0;
}

.cliente .search-and-filter .search-bar {
    width: 100%;
}

.location-icon:hover {
    color: $primary-color-hover;
    cursor: pointer;
}

.situacao-icon {
    margin-left: 20px;
}

.location-icon {
    color: $primary-color;
    &.disabled {
        opacity: 0.3;
    }
}

.map-el {
    min-height: 450px;
    flex-grow: 1;
}

.status-cliente {
    font-weight: 600;
    width: 100%;
    .tag-status {
        margin-bottom: 5px;
    }
}
</style>
